import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowUpRight,
  HeartHandshake,
  BookHeart,
  CupSoda,
  Search,
  Sparkles,
  Star,
} from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

// Map icon name strings (from backend) to lucide components
const iconMap = {
  HeartHandshake,
  BookHeart,
  CupSoda,
  Search,
  Sparkles,
  Star,
};

// Map accent name to a consistent palette of tailwind utilities so we never
// rely on dynamic class strings that Tailwind can't see at build time.
const accentMap = {
  primary: {
    iconBg: "bg-primary/15",
    iconColor: "text-primary",
    ring: "hover:border-primary/60",
    badge: "border-primary/30 text-primary",
    dot: "bg-primary",
    glow: "glow-card",
  },
  accent: {
    iconBg: "bg-accent/15",
    iconColor: "text-accent",
    ring: "hover:border-accent/60",
    badge: "border-accent/30 text-accent",
    dot: "bg-accent",
    glow: "glow-card glow-card-gold",
  },
  amber: {
    iconBg: "bg-amber-500/15",
    iconColor: "text-amber-400",
    ring: "hover:border-amber-400/60",
    badge: "border-amber-400/30 text-amber-300",
    dot: "bg-amber-400",
    glow: "glow-card glow-card-gold",
  },
  sky: {
    iconBg: "bg-sky-500/15",
    iconColor: "text-sky-400",
    ring: "hover:border-sky-400/60",
    badge: "border-sky-400/30 text-sky-300",
    dot: "bg-sky-400",
    glow: "glow-card",
  },
  rose: {
    iconBg: "bg-rose-500/15",
    iconColor: "text-rose-400",
    ring: "hover:border-rose-400/60",
    badge: "border-rose-400/30 text-rose-300",
    dot: "bg-rose-400",
    glow: "glow-card",
  },
  green: {
    iconBg: "bg-emerald-500/15",
    iconColor: "text-emerald-400",
    ring: "hover:border-emerald-400/60",
    badge: "border-emerald-400/30 text-emerald-300",
    dot: "bg-emerald-400",
    glow: "glow-card",
  },
};

export const FeaturedProjects = ({ projects }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  if (!projects || projects.length === 0) return null;

  return (
    <section
      id="featured"
      className="py-16 md:py-24 scroll-mt-24 relative"
      data-testid="featured-projects-section"
      aria-label="Favorite Projects"
      ref={ref}
    >
      <div className="mx-auto max-w-[var(--page-max)] px-4 md:px-6">
        <motion.div
          variants={container}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          {/* Header */}
          <motion.div variants={reveal} className="text-center mb-12">
            <span className="mono-label text-accent mb-3 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              Favorites
            </span>
            <h2
              className="text-2xl sm:text-3xl font-semibold mt-2"
              style={{ fontFamily: "var(--font-display)" }}
              data-testid="featured-projects-heading"
            >
              Favorite Projects
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mt-3 max-w-xl mx-auto">
              A small set of projects I&apos;m especially proud of — each one
              built with intention, for people I actually care about.
            </p>
          </motion.div>

          {/* 2x2 feature grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
            {projects.map((project) => {
              const accent = accentMap[project.accent] || accentMap.primary;
              const Icon = iconMap[project.icon] || Star;
              const testSlug = project.name
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");

              return (
                <motion.div key={project.name} variants={reveal}>
                  <Card
                    className={`group relative h-full flex flex-col p-7 md:p-9 bg-card border-border transition-colors duration-300 ${accent.glow} ${accent.ring}`}
                    data-testid={`featured-card-${testSlug}`}
                  >
                    {/* Top row: Icon + Type badge */}
                    <div className="flex items-start justify-between mb-5">
                      <div
                        className={`w-12 h-12 rounded-xl ${accent.iconBg} flex items-center justify-center`}
                        aria-hidden="true"
                      >
                        <Icon className={`w-6 h-6 ${accent.iconColor}`} />
                      </div>
                      <Badge
                        variant="outline"
                        className={`bg-transparent ${accent.badge} text-[10px] tracking-wider uppercase`}
                      >
                        {project.type}
                      </Badge>
                    </div>

                    {/* Name */}
                    <h3
                      className="text-xl md:text-2xl font-semibold leading-tight mb-2"
                      style={{ fontFamily: "var(--font-display)" }}
                      data-testid={`featured-name-${testSlug}`}
                    >
                      {project.name}
                    </h3>

                    {/* Tagline */}
                    {project.tagline && (
                      <p
                        className={`text-sm md:text-[15px] ${accent.iconColor} mb-4 leading-snug`}
                      >
                        {project.tagline}
                      </p>
                    )}

                    {/* Description */}
                    <p className="text-sm text-muted-foreground leading-[1.65] mb-5">
                      {project.description}
                    </p>

                    {/* Highlights */}
                    {project.highlights && project.highlights.length > 0 && (
                      <ul
                        className="space-y-2 mb-6"
                        aria-label={`${project.name} highlights`}
                      >
                        {project.highlights.map((h, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2.5 text-sm text-foreground/85"
                          >
                            <span
                              className={`mt-[7px] w-1.5 h-1.5 rounded-full ${accent.dot} flex-shrink-0`}
                              aria-hidden="true"
                            />
                            <span className="leading-snug">{h}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* CTA */}
                    <div className="mt-auto pt-2">
                      <Button
                        asChild
                        variant="secondary"
                        size="sm"
                        className={`bg-secondary text-secondary-foreground border border-border w-full sm:w-auto ${accent.ring}`}
                        data-testid={`featured-cta-${testSlug}`}
                      >
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`Visit ${project.name}`}
                        >
                          Visit {project.name}
                          <ArrowUpRight className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
